import { useEffect, useRef, useState } from "react";
import { Alert } from "@arco-design/web-react";
import type { ComponentProps } from "react";
import type { ItineraryNode, ItinerarySegment, TimeSlot } from "../types";
import type { RailwayInsertionDraft } from "../service/itineraryRailwayInsertionService";
import { ItineraryTransportWorkspace } from "./ItineraryTransportWorkspace";
import { railwayOvernightDays } from "../service/itineraryRailwayOvernightService";

type WorkspaceProps = ComponentProps<typeof ItineraryTransportWorkspace>;
type Props = {
  travelDate?: string | null;
  timeSlot: TimeSlot;
  majorId?: string | null;
  disabled?: boolean;
  selectionNote?: string;
  selectionMode?: "compare";
  footerContainer?: HTMLElement | null;
  getRailwayOptions: WorkspaceProps["getRailwayOptions"];
  getRailwayTransfers?: WorkspaceProps["getRailwayTransfers"];
  listCandidates: WorkspaceProps["listPointCandidates"];
  onCancel: () => void;
  onSubmit: (draft: RailwayInsertionDraft) => void;
  onDraftStateChange?: (dirty: boolean) => void;
};

/** 以独立局部草稿复用交通查询，确认前不向行程新增任何节点或区间。 */
export function ItineraryRailwayPicker({ travelDate, timeSlot, majorId, disabled, selectionNote, selectionMode, footerContainer, getRailwayOptions, getRailwayTransfers, listCandidates, onCancel, onSubmit, onDraftStateChange }: Props) {
  const [resolvingStay, setResolvingStay] = useState(false);
  const [stayError, setStayError] = useState<string | null>(null);
  const active = useRef(true);
  const pending = useRef(false);
  const revision = useRef(0);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const [draft] = useState(() => {
    const origin: ItineraryNode = { id: "railway-picker-origin", title: "", nodeType: "railway_station", timeSlot, sortOrder: 1 };
    const destination: ItineraryNode = { ...origin, id: "railway-picker-destination", sortOrder: 2 };
    const segment: ItinerarySegment = {
      id: "railway-picker-segment", startDayId: "railway-picker-day", endDayId: "railway-picker-day", routeSeq: 1,
      fromNodeId: origin.id, toNodeId: destination.id, routeType: "main_transport", transportMode: "rail", sourceType: "pending",
    };
    return { origin, destination, segment };
  });
  return <>{stayError ? <Alert type="error" content={stayError} /> : null}<ItineraryTransportWorkspace
    embeddedRailway visible confirmDisabled={disabled || resolvingStay}
    railwaySelectionNote={selectionNote} railwaySelectionMode={selectionMode} railwayFooterContainer={footerContainer}
    segment={draft.segment} fromNode={draft.origin} toNode={draft.destination}
    travelDate={travelDate} getRailwayOptions={getRailwayOptions} getRailwayTransfers={getRailwayTransfers}
    listPointCandidates={listCandidates} onCancel={() => { revision.current += 1; onCancel(); }} onDraftStateChange={(dirty) => {
      if (dirty) revision.current += 1;
      onDraftStateChange?.(dirty);
    }}
    onConfirm={(segment, changes = [], railwayTransfer) => {
      const origin = changes.find((change) => change.nodeId === draft.origin.id)?.draft;
      const destination = changes.find((change) => change.nodeId === draft.destination.id)?.draft;
      if (!origin || !destination) throw new Error("请选择火车方案的实际起终站");
      if (pending.current) return;
      const submission = { origin, destination, segment, railwayTransfer, timeSlot, majorId };
      const overnight = (railwayTransfer?.legs ?? [segment]).some((leg) => railwayOvernightDays(leg.departureAt, leg.arrivalAt));
      if (!overnight) { onSubmit(submission); return; }
      if (!listCandidates) { setStayError("无法查询“火车上”住宿点，请重新打开后重试"); return; }
      pending.current = true;
      const requestRevision = revision.current;
      setResolvingStay(true);
      setStayError(null);
      void listCandidates({ kind: "stay_area", keyword: "火车上", includeTrainOvernight: true, page: 1, pageSize: 20 })
        .then((result) => {
          if (!active.current || requestRevision !== revision.current) return;
          const stay = result.items.find((item) => item.refType === "stay_area" && item.name.trim() === "火车上");
          if (!stay) throw new Error("未找到启用的“火车上”住宿点，请维护住宿主数据后重试");
          onSubmit({ ...submission, trainStayAreaId: stay.refId });
        }).catch((error: unknown) => {
          if (active.current) setStayError(error instanceof Error ? error.message : "查询火车上住宿失败，请重试");
        }).finally(() => {
          pending.current = false;
          if (active.current) setResolvingStay(false);
        });
    }}
  /></>;
}
